<!--
	ToolGlyph — the icon set. Drawn, not licensed: every glyph is a literal picture
	of what the tool does to the geometry, in the same stroke weight the product
	draws with. They read as a CAD tool's rail rather than a marketing icon pack,
	which is the entire point.

	16×16 grid, 1.25 stroke, currentColor, no fills. Sized by the caller via
	font-size/width — never hardcode a size here.
-->
<script module lang="ts">
	export type GlyphName =
		| 'scan'
		| 'clean'
		| 'seam'
		| 'flatten'
		| 'refine'
		| 'cut'
		| 'wrap'
		| 'detect'
		| 'brush'
		| 'relief'
		| 'prestretch';
</script>

<script lang="ts">
	let { name, class: klass = '' }: { name: GlyphName; class?: string } = $props();
</script>

<svg
	class="glyph {klass}"
	viewBox="0 0 16 16"
	fill="none"
	stroke="currentColor"
	stroke-width="1.25"
	stroke-linecap="round"
	stroke-linejoin="round"
	aria-hidden="true"
	focusable="false"
>
	{#if name === 'scan'}
		<!-- A curved panel under scan lines. -->
		<path d="M2 11.5c3-4 9-4 12 0" />
		<path d="M2 8.5c3-4 9-4 12 0" opacity="0.55" />
		<path d="M2 5.5c3-4 9-4 12 0" opacity="0.3" />
	{:else if name === 'clean'}
		<!-- A triangle mesh: dense where it curves, coarse where it doesn't. -->
		<path d="M2 13l3.5-7 3 4.5 2.5-5L14 13z" />
		<path d="M5.5 6L8 10.5" opacity="0.5" />
		<path d="M11 5.5L14 13" opacity="0.5" />
	{:else if name === 'seam'}
		<!-- A seam drawn on a surface: dashed run, node where you placed it. -->
		<path d="M2 12c2.5-1 3.5-7 6-8" stroke-dasharray="2 1.75" />
		<path d="M8 4c2 1.5 3.5 5 6 6" stroke-dasharray="2 1.75" />
		<circle cx="8" cy="4" r="1.5" />
	{:else if name === 'flatten'}
		<!-- The core move: a curve dropping to a flat line. -->
		<path d="M2 5c3.5-3.5 8.5-3.5 12 0" />
		<path d="M8 7.75v3" opacity="0.6" />
		<path d="M6.5 9.5L8 11l1.5-1.5" opacity="0.6" />
		<path d="M2 13.25h12" />
	{:else if name === 'refine'}
		<!-- A Bézier with its anchors — the thing you grab. -->
		<path d="M2.5 11.5c2-6 9-6 11 0" />
		<rect x="1.25" y="10.25" width="2.5" height="2.5" rx="0.4" />
		<rect x="12.25" y="10.25" width="2.5" height="2.5" rx="0.4" />
		<path d="M8 5.5v-2" opacity="0.5" />
		<circle cx="8" cy="2.75" r="0.9" opacity="0.5" />
	{:else if name === 'cut'}
		<!-- A blade tracking a cut path. -->
		<path d="M2 12.5h12" stroke-dasharray="2.5 1.75" />
		<path d="M8 2.5l2.25 4.5a2.5 2.5 0 11-4.5 0z" />
		<path d="M8 9.5v3" />
	{:else if name === 'wrap'}
		<!-- Film returning around a panel edge. -->
		<path d="M3 4h6a4 4 0 014 4v0a4 4 0 01-4 4H6.5" />
		<path d="M8.5 9.5L6 12l2.5 2.5" />
		<path d="M3 4v8" opacity="0.5" stroke-dasharray="2 1.5" />
	{:else if name === 'detect'}
		<!-- A character line found, and marked. -->
		<path d="M1.75 10.5c3.5-1 8-4.5 12.5-8" opacity="0.55" />
		<circle cx="4.5" cy="9" r="1.25" />
		<circle cx="9" cy="5.75" r="1.25" />
		<path d="M2 14h12" opacity="0.3" />
	{:else if name === 'brush'}
		<!-- A stroke painted where you'd pull the film. -->
		<path d="M2.5 13.5c1.5.5 3-.5 3-2" />
		<path d="M5.5 11.5L12 4.5a1.75 1.75 0 012.5 2.5l-7 6.5" opacity="0.9" />
		<path d="M11 5.5l2 2" opacity="0.5" />
	{:else if name === 'relief'}
		<!-- A relief cut, and the stress draining out either side. -->
		<path d="M8 2v7" />
		<path d="M8 9l-1.5 2.5M8 9l1.5 2.5" />
		<path d="M3.5 5.5c-1 2-1 4.5 0 7" opacity="0.45" />
		<path d="M12.5 5.5c1 2 1 4.5 0 7" opacity="0.45" />
	{:else if name === 'prestretch'}
		<!-- Film racked outward, per zone. -->
		<rect x="5" y="5" width="6" height="6" rx="0.75" />
		<path d="M3 8H1M15 8h-2M8 3V1M8 15v-2" />
		<path d="M2.5 6.5L1 8l1.5 1.5M13.5 6.5L15 8l-1.5 1.5" opacity="0.6" />
	{/if}
</svg>

<style>
	.glyph {
		width: 1em;
		height: 1em;
		font-size: 1rem;
		flex: none;
	}
</style>
